import React, { useRef, useState } from 'react';

import { ExpandMoreButton } from '@components/ExpandMoreButton';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import { useCommonStyles, makeMuiStyles, ClassMap } from '@styles';
import clsx from 'clsx';
import Color from 'color';

const useStyles = makeMuiStyles((theme, common) => ({
  container: {
    borderSpacing: 0,
    color: theme.palette.text.secondary,
  },
  row: {
    ...common.middleText,
    borderBottomStyle: 'solid',
    borderBottomWidth: 1,
    borderBottomColor: theme.palette.grey['200'],
  },
  rowItem: {
    cursor: 'pointer',
    '&:hover': {
      background: Color(theme.palette.primary.light).alpha(0.2).string(),
    },
  },
  cell: {
    padding: '16px 0',
    textAlign: 'center',
  },
  columnDefRow: {
    background: theme.palette.grey['100'],
    color: theme.palette.text.primary,
    fontWeight: 400,
  },
  cellWrapper: {
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    overflow: 'hidden',
  },
  menuItemRoot: {
    ...common.middleText,
  },
}));

export type BaseRenderRow<RowType extends {}> = {
  [K in keyof RowType]?: (field: RowType[K], row: RowType) => React.ReactNode;
};

export interface ColumnMenuItem {
  label: string;
  selected?: boolean;
  onClick?: () => void;
}
export interface ColumnMenu {
  items: ColumnMenuItem[];
}
export interface ColumnDefinition<Field extends string> {
  field: Field;
  label: string;
  grow?: boolean;
  noWrapper?: boolean;
  menu?: ColumnMenu;
}

export interface DataGridProps<
  RowType extends Record<string, unknown>,
  RenderRow = BaseRenderRow<RowType>,
> {
  columns: ColumnDefinition<keyof RowType & string>[];
  rows: RowType[];
  renderRow: RenderRow;
  onClickRow?: (row: RowType) => void;
  emptyFallback?: React.ReactNode;
  classes?: ClassMap<'container'>;
}

export function DataGrid<RowType extends { id: any }>(props: DataGridProps<RowType>) {
  const {
    columns,
    rows,
    renderRow,
    onClickRow,
    emptyFallback,
    classes: customClasses = {},
  } = props;

  const menuAnchorsRef = useRef<Record<string, HTMLDivElement>>({});
  const [menuAnchors, setMenuAnchors] = useState<Record<string, HTMLDivElement | null>>({});

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  if (rows.length === 0 && emptyFallback) {
    return <>{emptyFallback}</>;
  }

  const renderColumn = (column: (typeof columns)[number]) => {
    const menuAnchor = menuAnchors[column.field];
    const isOpen = Boolean(menuAnchors[column.field]);

    const handleClose = () =>
      setMenuAnchors({
        ...menuAnchors,
        [column.field]: null,
      });

    return (
      <th
        ref={(div) => {
          menuAnchorsRef.current[column.field] = div!;
        }}
        key={column.field}
        className={classes.cell}
      >
        {column.label}
        {column.menu && (
          <ExpandMoreButton
            style={{ verticalAlign: 'middle', height: 18, width: 18 }}
            onClick={() =>
              setMenuAnchors({
                ...menuAnchors,
                [column.field]: isOpen ? null : menuAnchorsRef.current[column.field],
              })
            }
            expanded={isOpen}
          />
        )}
        {column.menu && (
          <Menu
            anchorEl={menuAnchor}
            keepMounted
            open={Boolean(menuAnchor)}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'center',
            }}
            getContentAnchorEl={undefined}
            elevation={4}
            disablePortal
          >
            {column.menu.items.map((item, i) => (
              <MenuItem
                key={i}
                selected={item.selected}
                classes={{ root: classes.menuItemRoot }}
                onClick={() => {
                  handleClose();
                  item.onClick?.();
                }}
              >
                {item.label}
              </MenuItem>
            ))}
          </Menu>
        )}
      </th>
    );
  };

  return (
    <table className={clsx(classes.container, customClasses.container)}>
      <tbody>
        <tr className={clsx(classes.row, classes.columnDefRow)}>{columns.map(renderColumn)}</tr>
        {rows.map((row, i) => {
          return (
            <tr
              key={i}
              className={clsx(classes.row, classes.rowItem)}
              onClick={() => !window.getSelection()?.toString() && onClickRow?.(row)}
            >
              {Object.entries(renderRow).map(([field, renderer]) => {
                const column = columns.find((c) => c.field === field);
                if (column === undefined) {
                  throw new Error(`${field} is not found in columns. `);
                }

                const content = renderer((row as any)[field], row);

                return (
                  <td key={field} className={classes.cell}>
                    {column.noWrapper ?? false ? (
                      content
                    ) : (
                      <div className={classes.cellWrapper}>
                        <span
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                          }}
                          style={{ cursor: 'text' }}
                        >
                          {content}
                        </span>
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
